import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MessageSquarePlus, ChevronUp, Trash2, Loader2, Lightbulb } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CATEGORIES = ["Improvement", "Bug", "Usability", "Other"];
const STATUS_META = {
  open: { label: "Open", cls: "bg-[#1A1A1A] text-[#A0A0A0] border-[#2A2A2A]" },
  planned: { label: "Planned", cls: "bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/30" },
  in_progress: { label: "In progress", cls: "bg-blue-500/10 text-blue-300 border-blue-500/30" },
  done: { label: "Done", cls: "bg-green-500/10 text-green-300 border-green-500/30" },
  declined: { label: "Declined", cls: "bg-red-500/10 text-red-300 border-red-500/30" },
};
const ADMIN_STATUSES = ["open", "planned", "in_progress", "done", "declined"];

export default function Feedback() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("top");
  const [form, setForm] = useState({ title: "", description: "", category: "Improvement" });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/feedback?status=${filter}&sort=${sort}`)
      .then((r) => setItems(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [filter, sort]);
  useEffect(load, [load]);

  const submit = async () => {
    if (form.title.trim().length < 3) { toast.error("Give your idea a clear title"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/feedback", form);
      setItems((cur) => [data, ...cur]);
      setForm({ title: "", description: "", category: "Improvement" });
      toast.success("Thanks for the feedback 🙏");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const vote = async (item) => {
    // optimistic
    setItems((cur) => cur.map((x) => x.id === item.id
      ? { ...x, voted: !x.voted, vote_count: x.vote_count + (x.voted ? -1 : 1) } : x));
    try {
      const { data } = await api.post(`/feedback/${item.id}/vote`);
      setItems((cur) => cur.map((x) => (x.id === item.id ? data : x)));
    } catch (err) { toast.error("Couldn't register your vote"); load(); }
  };

  const changeStatus = async (item, status) => {
    try {
      const { data } = await api.post(`/feedback/${item.id}/status`, { status });
      setItems((cur) => cur.map((x) => (x.id === item.id ? data : x)));
      toast.success("Status updated");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (item) => {
    if (!window.confirm("Delete this feedback?")) return;
    try {
      await api.delete(`/feedback/${item.id}`);
      setItems((cur) => cur.filter((x) => x.id !== item.id));
      toast.success("Deleted");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="feedback-page" className="max-w-3xl mx-auto px-5 md:px-8 py-10">
      <div className="overline">Help us improve</div>
      <h1 className="font-heading text-4xl sm:text-5xl gold-text leading-tight mt-1">Feedback board</h1>
      <p className="text-[#A0A0A0] mt-2 text-sm md:text-base max-w-xl">
        Tell us how we can make Pleasure Empire better — what's working, what's confusing, and anything that's not quite right. Upvote the feedback you agree with most.
      </p>

      {/* Submit */}
      <div data-testid="feedback-form" className="mt-8 rounded-2xl border border-[#D4AF37]/25 bg-[#111] p-5 space-y-3">
        <div className="flex items-center gap-2 text-white font-medium"><Lightbulb size={18} className="text-[#D4AF37]" /> Share your feedback</div>
        <input data-testid="feedback-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
          placeholder="In a few words — e.g. Chat is slow to load on mobile" className="pe-input w-full rounded-lg px-4 py-3" />
        <textarea data-testid="feedback-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
          rows={3} placeholder="Tell us more — what happened, and how it could be better (optional)" className="pe-input w-full rounded-lg px-4 py-3" />
        <div className="flex flex-wrap items-center gap-3">
          <select data-testid="feedback-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
            className="pe-input rounded-lg px-3 py-2.5 text-sm">
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <button data-testid="feedback-submit" onClick={submit} disabled={submitting}
            className="btn-gold rounded-full px-6 py-2.5 text-sm flex items-center gap-2 disabled:opacity-60 ml-auto">
            {submitting ? <Loader2 size={15} className="animate-spin" /> : <MessageSquarePlus size={15} />} Send feedback
          </button>
        </div>
        <p data-testid="feedback-disclaimer" className="text-[11px] text-[#6b6b6b] leading-relaxed pt-1">
          This board is for feedback on improving the existing app. By submitting, you agree your feedback may be used freely by Pleasure Empire to improve the platform, with no compensation, confidentiality, or ownership claim on your part.
        </p>
      </div>

      {/* Filters */}
      <div className="mt-8 flex flex-wrap items-center gap-2">
        {["all", "open", "planned", "in_progress", "done"].map((s) => (
          <button key={s} data-testid={`feedback-filter-${s}`} onClick={() => setFilter(s)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${filter === s ? "gold-gradient text-black border-transparent font-medium" : "border-[#2A2A2A] text-[#A0A0A0] hover:text-white"}`}>
            {s === "all" ? "All" : (STATUS_META[s]?.label || s)}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2 text-xs text-[#A0A0A0]">
          <span>Sort</span>
          <button data-testid="feedback-sort-top" onClick={() => setSort("top")} className={sort === "top" ? "text-[#D4AF37]" : "hover:text-white"}>Top</button>
          <span>·</span>
          <button data-testid="feedback-sort-new" onClick={() => setSort("new")} className={sort === "new" ? "text-[#D4AF37]" : "hover:text-white"}>Newest</button>
        </div>
      </div>

      {/* List */}
      <div className="mt-5 space-y-3">
        {loading ? (
          <div className="text-center py-12 text-[#D4AF37]"><Loader2 className="animate-spin inline" size={24} /></div>
        ) : items.length === 0 ? (
          <div data-testid="feedback-empty" className="text-center py-12 text-[#A0A0A0]">No feedback yet — be the first to tell us how to improve 🙏</div>
        ) : (
          items.map((item, i) => {
            const st = STATUS_META[item.status] || STATUS_META.open;
            const canDelete = isAdmin || item.author_id === user?.id;
            return (
              <motion.div key={item.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                data-testid={`feedback-item-${item.id}`} className="rounded-xl border border-[#2A2A2A] bg-[#111] p-4 flex gap-4">
                <button data-testid={`feedback-vote-${item.id}`} onClick={() => vote(item)}
                  className={`flex-shrink-0 w-14 rounded-lg border flex flex-col items-center justify-center py-2 transition-colors ${item.voted ? "gold-gradient text-black border-transparent" : "border-[#2A2A2A] text-[#A0A0A0] hover:text-white hover:border-[#D4AF37]/40"}`}>
                  <ChevronUp size={18} />
                  <span className="font-bold text-sm" data-testid={`feedback-votes-${item.id}`}>{item.vote_count}</span>
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white font-medium">{item.title}</span>
                    <span className={`text-[10px] uppercase tracking-wider border rounded px-1.5 py-0.5 ${st.cls}`}>{st.label}</span>
                    <span className="text-[10px] text-[#A0A0A0] border border-[#2A2A2A] rounded px-1.5 py-0.5">{item.category}</span>
                  </div>
                  {item.description && <p className="text-sm text-[#A0A0A0] mt-1 whitespace-pre-wrap">{item.description}</p>}
                  <div className="text-[11px] text-[#6b6b6b] mt-2">by {item.author_name}</div>
                  {(isAdmin || canDelete) && (
                    <div className="flex items-center gap-2 mt-3 flex-wrap">
                      {isAdmin && (
                        <select data-testid={`feedback-status-${item.id}`} value={item.status}
                          onChange={(e) => changeStatus(item, e.target.value)}
                          className="pe-input rounded-lg px-2 py-1 text-xs">
                          {ADMIN_STATUSES.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
                        </select>
                      )}
                      {canDelete && (
                        <button data-testid={`feedback-delete-${item.id}`} onClick={() => remove(item)}
                          className="text-xs text-[#A0A0A0] hover:text-[#E63946] flex items-center gap-1"><Trash2 size={13} /> Delete</button>
                      )}
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })
        )}
      </div>
    </div>
  );
}
